import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import { get, cloneDeep, set } from 'lodash';
import codemirror from '@/mixins/codemirror';
import iconsMixin from '../../components/core/dtwin/state/bridge/iconsMixin';
import multiSearchMixin from '@/mixins/multiSearchVirtualDevices';
import { versionsHasUnsyncedChanges, getUnsyncedChanges } from '../../utils/deviceTemplateUtils';
export default {
  name: 'DeviceTemplatesDetails',
  mixins: [iconsMixin, multiSearchMixin, codemirror],
  data() {
    return {
      accounts: [],
      account: null,
      parentTree: null,
      isDeviceMetaFormValid: true,
      isTemplateMetaFormValid: true,
      isDataStatesFormValid: true,
      useExpertMode: false,
      templateInformation: {
        templateIsChanged: false,
        isProductiveVersion: false,
        hasUnsyncedChanges: false,
        linkedDevicesTotalCount: -1,
        unsyncedChanges: []
      }
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    itemId() {
      return this.$route.params.id;
    },
    availableDurations() {
      const baseTranslation = 'DigitalTwin.DigitalTwin.visualization.timeRanges.count';
      return [15, 30].map(min => {
        return {
          value: min * 60,
          label: this.$i18n_tc(`${baseTranslation}Min`, min, {
            n: min
          })
        };
      }).concat([1, 3, 6, 12, 24].map(hour => {
        return {
          value: hour * 3600,
          label: this.$i18n_tc(`${baseTranslation}Hour`, hour, {
            n: hour
          })
        };
      })).concat([2, 7, 14].map(day => {
        return {
          value: day * 86400,
          label: this.$i18n_tc(`${baseTranslation}Day`, day, {
            n: day
          })
        };
      })).concat([1, 3, 6].map(mon => {
        return {
          value: mon * 30 * 86400,
          label: this.$i18n_tc(`${baseTranslation}Month`, mon, {
            n: mon
          })
        };
      })).concat([{
        value: 365 * 86400,
        label: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.1Y')
      }]);
    },
    deviceMetaInformationTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.deviceMetaInformation'),
        urlHashbang: 'tab-device-meta',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'deviceMetaInformation'
          }]
        }]
      };
    },
    templateMetaInformationTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.templateMetaInformation'),
        urlHashbang: 'tab-template-meta',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'templateMetaInformation'
          }]
        }]
      };
    },
    dataStatesTab() {
      return {
        label: this.$i18n_t('DigitalTwin.DigitalTwin.dataStates'),
        urlHashbang: 'tab-data-states',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'dataStates'
          }]
        }]
      };
    },
    ruleEditorTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.ruleEditor'),
        urlHashbang: 'tab-rule-editor',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'ruleEditor'
          }]
        }]
      };
    },
    dataRoutingTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.dataRouting'),
        urlHashbang: 'tab-data-routing',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'dataRouting'
          }]
        }]
      };
    },
    linkedDevicesTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.relatedDevices', {
          count: this.templateInformation.linkedDevicesTotalCount
        }),
        urlHashbang: 'linked-devices',
        eager: true,
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'linkedDevices'
          }]
        }]
      };
    },
    unsyncedChangesTab() {
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.unsyncedChanges'),
        urlHashbang: 'tab-unsynced-changes',
        rows: [{
          columns: [{
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'unsyncedChanges'
          }]
        }]
      };
    },
    tabs() {
      return [this.templateMetaInformationTab, this.deviceMetaInformationTab, this.dataStatesTab, this.ruleEditorTab, this.dataRoutingTab, this.healthConfigurationTab, this.linkedDevicesTab, this.unsyncedChangesTab];
    },
    formValidCompute() {
      return this.isDeviceMetaFormValid && this.isTemplateMetaFormValid;
    },
    linkedDevicesPredefinedSearch() {
      return [{
        param: 'deviceTemplate',
        search: {
          value: this.itemId
        }
      }];
    },
    isDeviceTemplateManager() {
      return this.permissions['DeviceTemplate.manage'];
    }
  },
  async created() {
    this.fetchAccounts();
  },
  mounted() {},
  methods: {
    get,
    async fetchItem(params) {
      try {
        let item = null;
        if (params && params.productiveVersion) {
          this.templateInformation.isProductiveVersion = true;
          item = await ApiService.deviceTemplateGetByIdProductive(this.itemId);
        } else {
          this.templateInformation.isProductiveVersion = false;
          item = await ApiService.deviceTemplateGetByIdDraft(this.itemId);
        }
        this.templateInformation.hasUnsyncedChanges = versionsHasUnsyncedChanges(item);
        this.templateInformation.unsyncedChanges = getUnsyncedChanges(item);
        if (!item.deviceMeta.connectorData) {
          item.deviceMeta.connectorData = {};
        }
        try {
          await this.buildDtwinTree(item);
        } catch (error) {
          if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
            this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
          } else {
            this.$snack(this.$i18n_t('Common.digitalTwins.twinNotFound'), 'error');
          }
          this.$router.push({
            path: '/device-templates'
          });
        }
        const metrics = get(item, 'healthDefinition.health', []);
        if (metrics.length > 0) {
          this.useExpertMode = !metrics.some(m => m.wizard);
        }
        return item;
      } catch (error) {
        console.error('Error fetching item:', error);
      }
    },
    updateUnsavedChanges(template) {
      this.templateInformation.unsyncedChanges = getUnsyncedChanges(template);
    },
    headline(item) {
      if (!item) {
        return;
      }
      return item.title || this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.deviceTemplate');
    },
    subtitle(item) {
      if (!item) {
        return;
      }
      return item.account || this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.editInformation');
    },
    async saveItem(item) {
      const healthBaseKey = 'healthDefinition.health';
      const metrics = get(item, `${healthBaseKey}`, []);
      set(item, healthBaseKey, metrics.map(m => ({
        ...m,
        wizard: !this.useExpertMode
      })));
      try {
        await ApiService.deviceTemplateUpdate(item);
      } catch (e) {
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.updateError'), 'error');
        throw e;
      }
      const template = await ApiService.deviceTemplateGetByIdDraft(item.id);
      this.templateInformation.hasUnsyncedChanges = versionsHasUnsyncedChanges(template);
      this.templateInformation.unsyncedChanges = getUnsyncedChanges(template);
      return template;
    },
    async fetchAccounts() {
      try {
        const accounts = await this.$store.dispatch('app/loadAccounts');
        this.accounts = accounts.filter(t => !t.isRootTenant);
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.failedToFetchAccounts'), 'error');
      }
    },
    ancestorList() {
      const extract = tree => {
        if (tree.children && tree.children.length > 0) {
          return [tree].concat(extract(tree.children[0]));
        }
        return [tree];
      };
      if (this.parentTree) {
        return cloneDeep(extract(this.parentTree).filter(i => i && i.id).concat([this.template]));
      }
      return [];
    },
    async buildDtwinTree(item) {
      let parentPartialTreePromise = ApiService.digitalTwinFetchPartialTree(this, item.parentId, undefined, undefined, ['no-children']);
      let parentObjectPromise = item.parentId ? ApiService.digitalTwinGet(this, item.parentId, true) : Promise.resolve({});
      return parentPartialTreePromise.then(result => {
        const tree = result;
        this.parentTree = get(tree, 'digitaltwins[0]', {});
        const ancestorList = this.ancestorList();
        const accountTitle = get(ancestorList, '[0].tenantName', undefined);
        const accountId = get(ancestorList, '[0].tenantId', undefined);
        if (accountTitle && accountId) {
          this.account = `${accountTitle} (ID: ${accountId})`;
        }
        return parentObjectPromise;
      });
    },
    handleDeviceMetaFormValidated(isValid) {
      this.isDeviceMetaFormValid = isValid;
    },
    handleTemplateMetaFormValidated(isValid) {
      this.isTemplateMetaFormValid = isValid;
    },
    handleDataStatesFormValidated(isValid) {
      this.isDataStatesFormValid = isValid;
    },
    setLinkedDevicesTotalCountItems(payload) {
      this.templateInformation.linkedDevicesTotalCount = payload.totalCount;
    },
    updateUnsyncedChanges(item) {
      this.templateInformation.hasUnsyncedChanges = versionsHasUnsyncedChanges(item);
      this.templateInformation.unsyncedChanges = getUnsyncedChanges(item);
    },
    healthConfigurationTab(item, originalItem) {
      const metrics = get(item, 'healthDefinition.health', []);
      const healthBaseKey = 'healthDefinition';
      return {
        label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.deviceHealthConfig'),
        urlHashbang: 'tab-health-configuration',
        rows: [{
          columns: [{
            field: {
              label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.expertMode'),
              type: 'switch',
              readonly: !this.isDeviceTemplateManager,
              value: this.useExpertMode,
              onInput: async ({
                updateItemByFieldKey,
                waitForUserConsent,
                rerenderInput
              }) => {
                const hasConsent = await waitForUserConsent(this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.swapExpertModeDialog.title'), this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.swapExpertModeDialog.message'));
                if (!hasConsent) {
                  return;
                }
                updateItemByFieldKey(`${healthBaseKey}.health`, metrics.map(m => ({
                  ...m,
                  wizard: this.useExpertMode
                })));
                this.useExpertMode = !this.useExpertMode;
                rerenderInput();
              }
            }
          }]
        }, ...(this.useExpertMode ? this.healthStateExpertTabRows(item, originalItem) : this.healthStateNormalTabRows(item, originalItem))]
      };
    },
    healthStateNormalTabRows(item) {
      const healthBaseKey = 'healthDefinition.health';
      const currentHealthStates = get(item, healthBaseKey, []);
      const valueField = state => {
        if (state.operator === 'VALUE_OLDER_THAN') {
          return {
            key: 'value',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.olderThanDuration'),
            type: 'select',
            options: this.availableDurations,
            required: true
          };
        }
        return {
          key: 'value',
          label: this.$i18n_t('DigitalTwin.DigitalTwin.states.value'),
          required: true,
          dataCy: 'value_'
        };
      };

      // const activeVersion = this.getActiveVersion(item);

      const wiredStateField = state => {
        if (state.operator === 'VALUE_OLDER_THAN') {
          return {
            key: 'wiredState',
            label: this.$i18n_t('DigitalTwin.DigitalTwin.states.dataState'),
            type: 'text',
            readonly: true,
            value: '_raw'
          };
        }
        return {
          key: 'wiredState',
          label: this.$i18n_t('DigitalTwin.DigitalTwin.states.dataState'),
          type: 'select',
          options: get(item, 'stateDefinition', []).filter(state => state.key !== '_health' && state.key !== '_parsed').map(state => {
            return {
              label: state.key,
              value: state.key
            };
          })
        };
      };
      const inputRows = [[{
        field: {
          key: 'type',
          label: this.$i18n_t('Common.type'),
          type: 'select',
          options: [{
            value: 'warning',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeWarning')
          }, {
            value: 'error',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeError')
          }]
        }
      }, {
        field: {
          key: 'category',
          label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.label'),
          type: 'select',
          dataCy: 'select_category_',
          options: [{
            value: 'battery',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.battery')
          }, {
            value: 'connectivity',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.connectivity')
          }, {
            value: 'device',
            label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.device')
          }]
        }
      }, {
        field: {
          key: 'description',
          label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.wizardDescriptionField'),
          dataCy: 'description_'
        }
      }], [{
        field: wiredStateField
      }, {
        field: {
          type: 'select',
          key: 'operator',
          label: this.$i18n_t('Consumers.Filter.createDialog.operator'),
          options: [{
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.valueOlderThan'),
            value: 'VALUE_OLDER_THAN'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.equals'),
            value: 'EQUALS'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.notEquals'),
            value: 'NOT_EQUALS'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.greaterThan'),
            value: 'GREATER_THAN'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.lessThan'),
            value: 'LESS_THAN'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.contains'),
            value: 'CONTAINS'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.notContains'),
            value: 'NOT_CONTAINS'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.isNull'),
            value: 'IS_NULL'
          }, {
            label: this.$i18n_t('Consumers.Filter.createDialog.operators.isNotNull'),
            value: 'IS_NOT_NULL'
          }]
        }
      }, {
        field: valueField
      }]];
      const healthStateRows = currentHealthStates.map((state, stateIndex) => {
        return {
          class: 'v-card v-sheet elevation-2 v-card__text',
          columns: [{
            rows: inputRows.map(inputRow => {
              const inputColumns = inputRow.map(input => {
                const computedField = typeof input.field === 'function' ? input.field(state) : input.field;
                return {
                  cols: input.cols,
                  sm: input.sm,
                  md: input.md,
                  lg: input.lg,
                  field: {
                    ...computedField,
                    dataCy: `${computedField.dataCy}${stateIndex}`,
                    key: `${healthBaseKey}[${stateIndex}].${computedField.key}`
                  }
                };
              });
              return {
                columns: inputColumns
              };
            })
          }, {
            class: 'flex-grow-0',
            rows: [{
              columns: [{
                field: {
                  type: 'button',
                  readonly: !this.isDeviceTemplateManager,
                  icon: 'fal fa-trash',
                  onClick: function (updateField) {
                    const newHealthStates = currentHealthStates.filter((_, index) => index !== stateIndex);
                    updateField(healthBaseKey, newHealthStates);
                  }
                }
              }]
            }]
          }]
        };
      });
      const addButtonRow = {
        columns: [{
          class: 'flex-grow-0 pl-0',
          field: {
            type: 'button',
            readonly: !this.isDeviceTemplateManager,
            icon: 'fal fa-plus',
            onClick: function (updateField) {
              let nextUnusedMetricID = 0;
              while (currentHealthStates.some(m => m.identifier === `metric_${nextUnusedMetricID}`)) {
                nextUnusedMetricID++;
              }
              const emptyState = {
                type: 'warning',
                // TODO: how to handle this?
                jsTransformer: '',
                wizard: true,
                operator: 'EQUALS',
                identifier: `metric_${nextUnusedMetricID}`,
                wiredState: '_raw',
                category: '',
                description: '',
                value: ''
              };
              updateField(`${healthBaseKey}`, [...currentHealthStates, emptyState]);
            }
          }
        }]
      };
      const healthStateRowsWithSpacers = [];
      healthStateRows.forEach((row, index) => {
        healthStateRowsWithSpacers.push({
          subtitle: currentHealthStates[index].identifier
        });
        healthStateRowsWithSpacers.push(row);
        if (index >= healthStateRows.length - 1) {
          return;
        }
        healthStateRowsWithSpacers.push({
          isDivider: true
        });
      });
      if (healthStateRowsWithSpacers.length === 0) {
        const emptyState = {
          columns: [{
            class: 'text-center',
            field: {
              type: 'emptystate',
              icon: 'fal fa-sensor-alert',
              title: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.healthState.emptyState.title'),
              description: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.healthState.emptyState.description'),
              readonly: !this.isDeviceTemplateManager,
              button: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.healthState.emptyState.buttonCreate'),
              onClick: function (updateField) {
                let nextUnusedMetricID = 0;
                while (currentHealthStates.some(m => m.identifier === `metric_${nextUnusedMetricID}`)) {
                  nextUnusedMetricID++;
                }
                const emptyState = {
                  type: 'warning',
                  // TODO: how to handle this?
                  jsTransformer: '',
                  wizard: true,
                  operator: 'EQUALS',
                  identifier: `metric_${nextUnusedMetricID}`,
                  wiredState: '_raw',
                  category: '',
                  description: '',
                  value: ''
                };
                updateField(`${healthBaseKey}`, [...currentHealthStates, emptyState]);
              }
            }
          }]
        };
        return [emptyState];
      }
      return [...healthStateRowsWithSpacers, addButtonRow];
    },
    healthStateExpertTabRows(item) {
      const healthBaseKey = 'healthDefinition';
      const selectedStatesPreview = Object.fromEntries(get(item, `${healthBaseKey}.stateKeys`, []).map(stateKey => {
        return [stateKey, {
          value: 'LAST_VALUE',
          time: 'ISO_TIMESTAMP_LAST_UPDATE'
        }];
      }));
      const currentMetrics = get(item, `${healthBaseKey}.health`, []);
      const dataStates = item.stateDefinition;
      const metricsRows = currentMetrics.map((metric, metricIndex) => {
        return {
          subtitle: metric.identifier,
          columns: [
          // type
          {
            cols: 12,
            sm: 12,
            md: 3,
            lg: 3,
            field: {
              label: this.$i18n_t('Common.type'),
              key: `${healthBaseKey}.health[${metricIndex}].type`,
              readonly: !this.isDeviceTemplateManager,
              type: 'select',
              options: [
              // TODO: add all options
              {
                value: 'warning',
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeWarning')
              }, {
                value: 'error',
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.typeError')
              }]
            }
          },
          // category
          {
            cols: 12,
            sm: 12,
            md: 3,
            lg: 3,
            field: {
              label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.label'),
              key: `${healthBaseKey}.health[${metricIndex}].category`,
              type: 'select',
              readonly: !this.isDeviceTemplateManager,
              options: [
              // TODO: add all options
              {
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.battery'),
                value: 'battery'
              }, {
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.connectivity'),
                value: 'connectivity'
              }, {
                label: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.category.value.device'),
                value: 'device'
              }]
            }
          },
          // identifier
          {
            cols: 12,
            sm: 12,
            md: 5,
            lg: 5,
            isSpacer: true
          },
          // trash button
          {
            cols: 12,
            sm: 12,
            md: 1,
            lg: 1,
            field: {
              type: 'button',
              readonly: !this.isDeviceTemplateManager,
              icon: 'fal fa-trash',
              onClick: function (updateField) {
                updateField(`${healthBaseKey}.health`, currentMetrics.filter((_, index) => index !== metricIndex));
              }
            }
          }, {
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            slotName: 'healthStateExpertModeMetricCodeEditor',
            field: {
              readonly: !this.isDeviceTemplateManager,
              key: `${healthBaseKey}.health[${metricIndex}].jsTransformer`
            }
          }]
        };
      });
      return [
      // select states
      {
        subtitle: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.metricDescription'),
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 12,
          field: {
            label: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.healthState.stateKeys'),
            key: `${healthBaseKey}.stateKeys`,
            type: 'autocomplete',
            readonly: !this.isDeviceTemplateManager,
            multiple: true,
            options: (dataStates || []).filter(state => state.key !== '_health' && state.key !== '_parsed').map(s => ({
              label: s.key,
              value: s.key
            }))
          }
        }]
      },
      // quick tutorial / example
      {
        isExpansionPanel: true,
        headline: this.$i18n_t('NiotaClassic.DeviceTypes.form.parser.quickReferenceHeader'),
        subtitle: this.$i18n_t('DigitalTwin.VirtualDevice.state.health.metricExample'),
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 12,
          field: {
            readonly: !this.isDeviceTemplateManager,
            type: 'code',
            fullWidth: true,
            content: `function (params) {
  // get battery voltage from params
  const batteryVoltage = params['battery_voltage'].value;

  // battery voltage is below 2.5V
  if (batteryVoltage < 2.5) {
    return "Battery almost empty";
  }

  // get current time/date
  const now = new Date();

  // 24hrs * 3600s/day * 1000ms/s
  const 24hoursInMilliseconds = 24 * 3600 * 1000;

  // get the last heartbeat time from params
  const lastHeartbeatTime = new Date(params['heartbeat'].time);

  // has not sent a heartbeat within 24 hours
  if ((now - lastHeartbeatTime) > 24hoursInMilliseconds) {
    return "Device seems offline";
  }

  return null;
}`
          }
        }]
      },
      // params preview
      {
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 12,
          field: {
            readonly: !this.isDeviceTemplateManager,
            type: 'code',
            fullWidth: true,
            content: `params = ${JSON.stringify(selectedStatesPreview, null, 4)};`
          }
        }]
      }, ...metricsRows.map((row, rowIndex) => metricsRows.length > 1 && rowIndex !== metricsRows.length - 1 ? [row, {
        isDivider: true
      }] : [row]).flat(),
      // add another metric button
      {
        columns: [{
          cols: 12,
          sm: 12,
          md: 12,
          lg: 8,
          field: {
            type: 'button',
            readonly: !this.isDeviceTemplateManager,
            icon: 'fal fa-plus',
            onClick: function (updateField) {
              let nextUnusedMetricID = 0;
              while (currentMetrics.some(m => m.identifier === `metric_${nextUnusedMetricID}`)) {
                nextUnusedMetricID++;
              }
              updateField(`${healthBaseKey}.health`, [...currentMetrics, {
                identifier: `metric_${nextUnusedMetricID}`,
                wizard: false,
                wiredState: '_raw'
              }]);
            }
          }
        }]
      }];
    }
  }
};